import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { BaseService } from '@core';
import {
  AppFile,
  Translation,
  TranslationCoverage,
  TranslationInput,
  TranslationKind,
  TranslationLanguage,
} from '@gqlSchema';
import { TranslateService } from '@ngx-translate/core';
import { DocumentNode } from 'graphql';
import { PrimeNGConfig } from 'primeng/api';
import { map, Observable, switchMap, tap, Unsubscribable } from 'rxjs';
import { FileUploadService } from '../../../core/services/file-upload.service';
import {
  mutationCreateUpdateTranslation,
  mutationExportTranslations,
  mutationImportTranslations,
  SelectAllFields,
  selectByKey,
  selectByLanguageQuery,
  SelectDeleteRestoreFields,
  SelectOneFields,
  translationCoverageQuery,
} from '../graphql/translation.fragments';

@Injectable({
  providedIn: 'root',
})
export class TranslationService extends BaseService<TranslationLanguage> {
  tt = inject(TranslateService);
  private http = inject(HttpClient);
  private primeConfig = inject(PrimeNGConfig);
  private fileUpload = inject(FileUploadService);

  protected override selectOneFields: DocumentNode = SelectOneFields;
  protected override selectAllFields: DocumentNode = SelectAllFields;
  protected override deleteRestoreFields: DocumentNode =
    SelectDeleteRestoreFields;

  loadedTranslations = false;
  /** ISO code (as stored in the languages table, e.g. 'SR') of the language the UI currently shows. */
  currentLanguage = signal<string>('');
  translations$!: Unsubscribable;

  constructor() {
    super();
    this.defaultPageSize = 25;
    this.pageSizes = [25, 50, 100];
    this.pageRequest.offset = 0;
    this.pageRequest.limit = this.defaultPageSize;
    this.initGQL({ names: { oneName: 'translation' } });
  }

  /** Always from the server — a cached copy missed the languages added since it was read. */
  public getTranslationsByKey(key: string): Observable<Translation[]> {
    return this.apollo
      .query<{ translationsByKey: Translation[] }>({
        query: selectByKey,
        variables: { key },
        fetchPolicy: 'network-only',
      })
      .pipe(map((res) => res.data?.translationsByKey ?? []));
  }

  /** Per language: how many keys (of this kind, or all) have a value. */
  public getCoverage(kind: TranslationKind | null): Observable<TranslationCoverage[]> {
    return this.apollo
      .query<{ translationCoverage: TranslationCoverage[] }>({
        query: translationCoverageQuery,
        variables: { kind },
        fetchPolicy: 'network-only',
      })
      .pipe(map((res) => res.data?.translationCoverage ?? []));
  }

  /** Every translation as a CSV file (one row per key and language), downloaded straight away. */
  public exportCsv(): Observable<AppFile | null> {
    return this.mutate<AppFile | null>({ mutation: mutationExportTranslations }).pipe(
      tap((file) => {
        if (file) this.fileUpload.downloadAppFile(file.id, file.name ?? 'translations.csv');
      }),
    );
  }

  /** Uploads a CSV in the export's format and writes every row of it (new rows or overwrites). */
  public importCsv(file: File): Observable<boolean> {
    const formData = new FormData();
    formData.append('file', file);
    return this.fileUpload.postFiles(formData).pipe(
      switchMap((files) =>
        this.mutate<boolean>({
          mutation: mutationImportTranslations,
          variables: { fileId: files[0].id },
        }),
      ),
    );
  }

  public createUpdateTranslation(
    item: TranslationInput[],
  ): Observable<TranslationLanguage> {
    return this.mutate({
      mutation: mutationCreateUpdateTranslation,
      variables: { item },
    }).pipe(
      tap((e) => {
        this.updateCache(e as any);
      }),
    );
  }

  public updateCache(trans: Translation[]) {
    let query: any = this.apollo.client.cache.readQuery({
      query: this.selectAllQuery,
      variables: {
        pageRequest: this.pageRequest,
        filterRequest: this.filterRequest,
      },
    });

    if (!query) return;

    let wq: {
      translations: {
        data: TranslationLanguage[];
        totalCount: number;
      };
    } = JSON.parse(JSON.stringify(query));

    let i = wq.translations.data.findIndex(
      (e: TranslationLanguage) => e.key === trans[0].key,
    );

    if (i >= 0 && wq.translations.data[i].translations)
      wq.translations.data[i].translations = [...trans];

    this.apollo.client.cache.writeQuery({
      query: this.selectAllQuery,
      variables: {
        pageRequest: this.pageRequest,
        filterRequest: this.filterRequest,
      },
      data: wq,
    });
  }

  /** Switch the UI language: cached rows show at once, the fresh rows follow from the server. */
  public refetchTranslationsByLanguage(isoCode2: string) {
    this.getTranslations(isoCode2);
  }

  langRef: any;
  public getTranslations(langCode: string = 'SR') {
    var _trans;
    try {
      _trans = JSON.parse(
        localStorage.getItem(`__translations_${langCode}`) as string,
      );
    } catch (error) {
      localStorage.removeItem(`__translations_${langCode}`);
    }
    if (_trans) {
      this.fillUpTranslations(langCode, _trans);
    }
    this.translations$?.unsubscribe();
    this.langRef = this.apollo.watchQuery({
      query: selectByLanguageQuery,
      variables: { isoCode2: langCode },
    });

    this.translations$ = this.langRef.valueChanges
      .pipe(
        map((result: any) => {
          if (!result.data || !result.data.translationsByLanguage) {
            return;
          }

          const translations: any = {};
          result.data.translationsByLanguage.map((tr: any) => {
            translations[tr.key] = tr.value;
          });

          localStorage.setItem(
            `__translations_${langCode}`,
            JSON.stringify(translations),
          );
          this.fillUpTranslations(langCode, translations);
        }),
      )
      .subscribe(() => {});
  }

  private fillUpTranslations(langCode: string, translations: any) {
    this.dbTranslations.set(langCode, translations);
    const statics = this.staticTranslations.get(langCode);
    // Database rows win over the bundled JSON — the file only fills in what the DB has no row for.
    this.tt.setTranslation(langCode, statics ? { ...statics, ...translations } : translations);

    this.tt.setDefaultLang(langCode);
    this.tt.use(langCode);
    this.loadedTranslations = true;
    this.currentLanguage.set(langCode);
    this.applyPrimeNgCalendarNames();

    if (!statics) this.loadStaticTranslations(langCode);
  }

  /** Per language: the rows fetched from the database and the bundled assets/i18n/<lang>.json (plural forms). */
  private dbTranslations = new Map<string, Record<string, string>>();
  private staticTranslations = new Map<string, Record<string, string>>();

  private loadStaticTranslations(langCode: string) {
    this.http
      .get<Record<string, string>>(`assets/i18n/${langCode.toLowerCase()}.json`)
      .subscribe({
        next: (json) => {
          this.staticTranslations.set(langCode, json);
          if (langCode !== this.currentLanguage()) return;
          this.tt.setTranslation(langCode, { ...json, ...this.dbTranslations.get(langCode) });
          this.applyPrimeNgCalendarNames();
        },
        // No file for this language — the DB rows are all there is.
        error: () => this.staticTranslations.set(langCode, {}),
      });
  }

  /**
   * PrimeNG's calendar/datepicker names are set once at bootstrap from the build locale
   * (Serbian on Balkan builds); re-derive them from the translation keys the rest of the app
   * uses ("Monday", "January", …) so the datepicker follows the UI language. Date format and
   * first day of the week stay with the locale config.
   */
  private applyPrimeNgCalendarNames() {
    const t = (key: string) => this.tt.instant(key);
    const dayNames = WEEKDAY_KEYS.map(t);
    const monthNames = MONTH_KEYS.map(t);
    this.primeConfig.setTranslation({
      dayNames,
      dayNamesShort: dayNames.map((d) => d.slice(0, 3)),
      dayNamesMin: dayNames.map((d) => d.slice(0, 2)),
      monthNames,
      monthNamesShort: monthNames.map((m) => m.slice(0, 3)),
      today: t('Today'),
      clear: t('Clear'),
    });
  }
}

const WEEKDAY_KEYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_KEYS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
